import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "326",
  "slug": "map-projections-explained-beyond-mercator",
  "title": "Beyond Mercator: 6 Map Projections and What Each One Distorts",
  "excerpt": "Every flat map lies about something — area, shape, distance, or direction. Here are six map projections beyond Mercator, what each one preserves, what it sacrifices, and when to use it.",
  "date": "2026-06-02",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1524661135-423995f22d0b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhbnRpcXVlJTIwd29ybGQlMjBtYXB8ZW58MXwwfHx8MTc4MDM5MzI3Mnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "There is a simple, unavoidable truth at the heart of cartography: you cannot flatten a sphere without breaking something. Peel an orange and try to press the skin flat — it tears, stretches, and buckles. The Earth's surface behaves the same way. Every flat map is a compromise, and every map projection is a decision about which compromise to make."
    },
    {
      "type": "paragraph",
      "text": "Projections trade off four properties, and no flat map can keep all of them at once:"
    },
    {
      "type": "list",
      "items": [
        "Area — whether two regions that are equal in real life look equal on the map.",
        "Shape — whether coastlines and countries keep their true form (this is called conformality).",
        "Distance — whether measured gaps on the map match real distances.",
        "Direction — whether compass bearings stay true."
      ]
    },
    {
      "type": "paragraph",
      "text": "A projection that perfectly preserves area is called equal-area; one that preserves shape is conformal; and you can never have both at once. Most maps are either one or the other, or a 'compromise' that does neither perfectly but keeps every error small. Mercator is famous, but it's only one answer among many. Here are six projections and the bargain each one strikes."
    },
    {
      "type": "heading",
      "text": "1. Mercator: True Angles, Wildly Wrong Sizes"
    },
    {
      "type": "paragraph",
      "text": "The Mercator projection (1569) is conformal — it preserves shape and direction locally, which is why a compass bearing is a straight line and why it powers nearly every web map for street-level navigation. The price is area: it inflates everything toward the poles, which is why Greenland looks the size of Africa when Africa is roughly fourteen times larger. Brilliant for sailing and street navigation, badly misleading as a picture of the world."
    },
    {
      "type": "heading",
      "text": "2. Gall-Peters: Honest Sizes, Squashed Shapes"
    },
    {
      "type": "paragraph",
      "text": "The Gall-Peters projection is equal-area: every country appears at its true relative size, so Africa and South America finally look as enormous as they are. It became a political talking point in the 1970s as a deliberate rebuke to Mercator. The trade-off is obvious at a glance — shapes are stretched and distorted, with landmasses looking oddly elongated near the equator. It tells the truth about area by lying about form."
    },
    {
      "type": "heading",
      "text": "3. Robinson: A Deliberate Compromise"
    },
    {
      "type": "paragraph",
      "text": "Arthur Robinson designed his projection in 1963 to look right rather than be right in any single property. It doesn't perfectly preserve area, shape, or distance — instead it spreads the error around so that no part of the map looks badly wrong. National Geographic adopted it for world maps in 1988. It's the cartographic equivalent of a sensible all-rounder: nothing's perfect, but nothing's glaringly off either."
    },
    {
      "type": "heading",
      "text": "4. Winkel Tripel: The Modern Standard"
    },
    {
      "type": "paragraph",
      "text": "The Winkel Tripel projection takes its name from the German word for 'triple', because it aims to minimise three distortions at once — area, direction, and distance. National Geographic switched to it for world maps in 1998, and it's now the default choice for a general-purpose, good-looking world map. It's another compromise projection, but a more mathematically refined one than Robinson, with the poles rendered as gentle curves rather than points or lines."
    },
    {
      "type": "heading",
      "text": "5. Mollweide: Equal-Area for the Whole Globe"
    },
    {
      "type": "paragraph",
      "text": "The Mollweide projection is an equal-area design shaped like an ellipse, with curved meridians sweeping to the poles. Because it keeps area honest across the entire map, it's a favourite for showing global distributions — climate zones, population, the spread of a phenomenon across the whole planet — where comparing the true size of regions matters. Shapes near the edges get stretched, the usual cost of preserving area, but the overall impression of the world's proportions is sound."
    },
    {
      "type": "heading",
      "text": "6. AuthaGraph: A 21st-Century Attempt"
    },
    {
      "type": "paragraph",
      "text": "AuthaGraph, devised by Japanese architect Hajime Narukawa, divides the globe into 96 triangles, maps them onto a tetrahedron, and unfolds the result into a near-rectangle. The payoff is a map that keeps the relative sizes of continents and oceans remarkably close to reality with little visible distortion, and it can even be tiled seamlessly in any direction. The catch is that it can be disorienting — the familiar classroom layout is gone — and it isn't strictly equal-area. It won Japan's Good Design Grand Award in 2016 and shows that the centuries-old projection problem is still being chipped at."
    },
    {
      "type": "heading",
      "text": "So Which Projection Should You Use?"
    },
    {
      "type": "paragraph",
      "text": "There is no universally best projection — only the best one for a job. Navigating a city street? Mercator. Comparing the true size of countries in a classroom? An equal-area projection like Gall-Peters or Mollweide. A clean, balanced wall map? Winkel Tripel or Robinson. Mapping the route of a long-haul flight? A projection centred on the poles will show the great-circle path far better than Mercator. The moment you understand that the choice is a trade-off, you stop asking which map is 'correct' and start asking which distortion you can live with."
    }
  ]
};
